import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon, PersonaIcon } from '@persona-id/design-tokens/icons';
import { omit, snakeCase, sortBy, startCase } from 'lodash/fp';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import PlatformSchemaEditor from 'dashboard/components/PlatformSchemaEditor';
import JsonSchemaPreview from 'dashboard/components/WorkflowControlPanel/JsonSchemaPreview';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { getTypeDisplayText, PlatformJsonSchema } from 'dashboard/lib/workflows/platform-object';
import { WorkflowError } from 'dashboard/models/workflow';
import { BlockInput } from 'dashboard/models/workflow-block';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { headingMixins, paragraphMixins } from 'design-system/mixins/text';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';
import { generateWorkflowToken } from 'lib/workflows';

const SubTitle = styled.div`
  ${headingMixins.sm}
  padding-bottom: ${Spacing[2]};
`;

const InputName = styled.dt.attrs<{ hasError: boolean }>(({ hasError }) => ({
  'aria-invalid': hasError,
}))<{ hasError: boolean }>`
  ${paragraphMixins.mdBold}
  color: ${({ hasError }) => (hasError ? Color.TextDanger : 'inherit')};
  margin-top: ${Spacing[3]};
`;

const InputType = styled.dd`
  ${paragraphMixins.md}
  margin-top: ${Spacing[1]};
  overflow-x: auto;
`;

interface BlockInputsConfigProps {
  editable: boolean;
  errors: WorkflowError[];
  inputs: BlockInput[];
  handleClose: () => void;
  updateInputs: (inputs: BlockInput[]) => void;
}

const BlockInputsConfig: FC<BlockInputsConfigProps> = ({
  editable,
  errors,
  inputs,
  handleClose,
  updateInputs,
}) => {
  const [draftInputs, setDraftInputs] = useState<BlockInput[]>([]);
  const [inputsUpdated, setInputsUpdated] = useState(false);

  const parsedInputs = useMemo(() => {
    return sortBy(
      'name',
      inputs.map((input) => ({
        ...input,
        schema: JSON.parse(input.schema),
      })),
    );
  }, [inputs]);

  const inputsSchema: JsonSchemaObject<PlatformJsonSchema> = useMemo(() => {
    return parsedInputs.reduce(
      (combinedSchema: JsonSchemaObject<PlatformJsonSchema>, { token, name, schema, required }) => {
        const formattedName = snakeCase(name);
        combinedSchema.properties[formattedName] = {
          ...schema,
          id: token,
        };
        if (required && combinedSchema.required) {
          combinedSchema.required.push(formattedName);
        }
        return combinedSchema;
      },
      { type: JsonSchemaTypes.Object, properties: {}, required: [] },
    );
  }, [parsedInputs]);

  const updateInputSchema = (newInputsSchema: JsonSchemaObject<PlatformJsonSchema>) => {
    const newInputs: BlockInput[] = Object.entries(newInputsSchema.properties).map(
      ([name, schema]) => ({
        token: schema.id || generateWorkflowToken(),
        name,
        schema: JSON.stringify(omit('id', schema)),
        required: (newInputsSchema.required || []).includes(name),
      }),
    );
    setDraftInputs(newInputs);
    setInputsUpdated(true);
  };

  const [isInputEditorOpen, { disable: closeInputEditor, toggle: toggleInputEditor }] =
    useToggle(false);

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError<`${string}.${string}`>(errors);

  const onCloseInputsPanel = useCallback(() => {
    closeInputEditor();
    inputsUpdated && updateInputs(draftInputs);
    setDraftInputs([]);
    setInputsUpdated(false);
  }, [draftInputs, inputsUpdated, updateInputs, closeInputEditor]);

  return (
    <>
      <div>
        <SubTitle>Input Step</SubTitle>
        <Spacer height={2} />
        <StepConfigTypeCard
          icon={<PersonaIcon size={24} />}
          title='Input'
          description='Specify the input parameters'
        />
        <Spacer height={2} />
        <SubTitle>Configuration</SubTitle>
        <ParamLabel
          labelText='Input Schema'
          htmlFor='block-inputs-config__input-schema'
          buttons={[
            <Button
              key='edit'
              width='icon'
              variant='basic'
              disabled={!editable}
              onClick={toggleInputEditor}
              id='block-inputs-config__input-schema'
            >
              <EditIcon />
            </Button>,
          ]}
        />
        {isInputEditorOpen && (
          <PlatformSchemaEditor
            firstColumnName='input'
            jsonSchema={inputsSchema}
            templateOptionsByObjectKey={{}}
            updateJsonSchema={updateInputSchema}
            includePersonaObjects
            panelTitle='Input Schema'
            panelSubtitle='Specify the key and type for each input. The type will allow downstream steps to interact with the input.'
            onClosePanel={onCloseInputsPanel}
          />
        )}
        <Paragraph>
          {parsedInputs.length
            ? `${parsedInputs.length} ${parsedInputs.length === 1 ? 'input' : 'inputs'} configured`
            : 'No inputs configured'}
        </Paragraph>
        <dl role='list'>
          {parsedInputs.map(({ token, name, schema }) => {
            const suffix =
              schema.type === JsonSchemaTypes.Array && getTypeDisplayText(schema.items)
                ? ` of ${startCase(getTypeDisplayText(schema.items))}`
                : '';
            return (
              <Fragment key={token}>
                <InputName hasError={hasErrorFor(`inputs.${token}`)}>{startCase(name)}</InputName>
                <InputType>
                  Type: {startCase(getTypeDisplayText(schema))}
                  {suffix}
                </InputType>
                {editable && renderErrorMessageFor(`inputs.${token}`, { narrow: true })}
              </Fragment>
            );
          })}
        </dl>
        <Spacer height={3} />
        <JsonSchemaPreview
          id='block-inputs-config__inputs-schema'
          jsonSchema={inputsSchema['properties']}
          requiredFields={inputsSchema['required']}
          onClick={toggleInputEditor}
        />
      </div>
      <Spacer height={3} />
      <HStack equal={true}>
        <Button
          id='block-inputs-config__close-button'
          aria-label='Close panel'
          onClick={handleClose}
        >
          Close
        </Button>
      </HStack>
    </>
  );
};

export default BlockInputsConfig;
